import {
  AspectRatio,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Modal,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useState } from "react";
import {
  Collab,
  CollabContent,
  UserType,
} from "../../../../../../gql/__generated__/graphql";
import GenericConfirmationModal from "../../../../../components/Modals/GenericConfirmationModal";

interface ReviewContentModalProps {
  opened: boolean;
  onClose: () => void;
  collab: Collab;
  userRole: UserType;
  onAccept: (contentId: string) => void;
  onRequestChanges: () => void;
}

function ReviewContentModal({
  opened,
  onClose,
  collab,
  userRole,
  onAccept,
  onRequestChanges,
}: ReviewContentModalProps) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState<string | null>(
    null
  );
  const submittedContent = collab.submittedContent;
  const approvedContentCount =
    submittedContent?.filter((c) => c.status === "approved").length ?? 0;
  const totalContentCount = collab.submittedContent?.length ?? 0;

  const handleAcceptClick = (contentId: string) => {
    if (approvedContentCount + 1 === totalContentCount) {
      setSelectedContentId(contentId);
      setShowConfirmation(true);
    } else {
      onAccept(contentId);
    }
  };

  const handleConfirmAccept = () => {
    if (selectedContentId) {
      onAccept(selectedContentId);
    }
    setShowConfirmation(false);
    onClose();
  };

  const renderContent = (content: CollabContent) => {
    const isVideo = content.contentUrl.includes("video");
    const isApproved = content.status === "approved";

    return (
      <Grid.Col span={{ base: 12, md: 6 }} key={content.id}>
        <Card withBorder p="md">
          <Stack>
            {isVideo ? (
              <AspectRatio ratio={9 / 16}>
                <video
                  src={content.contentUrl}
                  controls
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </AspectRatio>
            ) : (
              <AspectRatio ratio={16 / 9}>
                <img
                  src={content.contentUrl}
                  alt="Submitted content"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </AspectRatio>
            )}
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Submitted on:{" "}
                {new Date(content.submittedAt).toLocaleDateString()}
              </Text>
              {isApproved && <Badge color="green">Approved</Badge>}
            </Group>
            {userRole === "BRAND_USER" && !isApproved && (
              <Group justify="flex-end">
                <Button
                  variant="outline"
                  color="primary"
                  onClick={onRequestChanges}
                >
                  Request Changes
                </Button>
                <Button
                  color="secondary"
                  onClick={() => handleAcceptClick(content.id)}
                >
                  Accept Content
                </Button>
              </Group>
            )}
          </Stack>
        </Card>
      </Grid.Col>
    );
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={<Title order={3}>Review Submitted Content</Title>}
        size="xl"
        centered
      >
        <Stack>
          {submittedContent && submittedContent.length > 0 ? (
            <Grid>{submittedContent.map(renderContent)}</Grid>
          ) : (
            <Text>No content has been submitted for this collab yet.</Text>
          )}
        </Stack>
      </Modal>
      <GenericConfirmationModal
        opened={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        title="Final Acceptance"
        text="This is the last content to accept. Accepting this will close out the Collab and pay the Creator."
        onConfirm={handleConfirmAccept}
        confirmText="Accept and Complete"
      />
    </>
  );
}

export default ReviewContentModal;
